import { useMemo } from 'react';
import { Stack, Paper, Typography, Drawer, Alert, Box } from '@mui/material';
import { type Control, type FieldErrors, type UseFormSetValue } from 'react-hook-form';

import { ControlledSingleSelect, ControlledTextField, ErrorMessage } from '@/src/ui';
import type { QtiHotspotQuestionDTO, HotspotRegion, HotspotMatchMode } from '@/src/lib';

interface HSScoringEditorProps {
    control: Control<QtiHotspotQuestionDTO>;
    errors: FieldErrors<QtiHotspotQuestionDTO>;
    regions: HotspotRegion[];
    targetWeight: number;
    matchMode: HotspotMatchMode;
    maxSelections: number;
    setValue: UseFormSetValue<QtiHotspotQuestionDTO>;
}

export default function HSScoringEditor({
    control,
    errors,
    regions,
    targetWeight,
    matchMode,
    maxSelections,
}: HSScoringEditorProps) {
    // ── Derived info ───────────────────────────────────────────────────
    const maxPossibleWeight = useMemo(
        () =>
            (regions ?? []).reduce(
                (sum, r) => sum + (Number(r.weight) > 0 ? Number(r.weight) : 0),
                0
            ),
        [regions]
    );

    const hasNegativeWeights = useMemo(
        () => (regions ?? []).some((r) => Number(r.weight) < 0),
        [regions]
    );

    // ── Warnings ───────────────────────────────────────────────────────
    const warnings: string[] = [];

    if (matchMode === 'exact' && targetWeight > maxPossibleWeight) {
        warnings.push(
            `Target weight (${targetWeight}) exceeds the maximum possible score from all positive-weight regions (${maxPossibleWeight}).`
        );
    }

    const positiveRegionCount = (regions ?? []).filter((r) => Number(r.weight) > 0).length;
    if (positiveRegionCount > maxSelections) {
        // Not necessarily wrong, but worth noting
    }
    if (maxSelections < 1) {
        warnings.push('Max selections must be at least 1.');
    }

    // Check if uniform weight (simple counting scenario)
    const uniquePositiveWeights = new Set(
        (regions ?? []).filter((r) => Number(r.weight) > 0).map((r) => Number(r.weight))
    );
    const isSimpleCounting = uniquePositiveWeights.size <= 1 && !hasNegativeWeights;

    return (
        <Drawer
            variant="permanent"
            anchor="right"
            slotProps={{ paper: { sx: { position: 'static', minWidth: 700, p: 0 } } }}
            open
        >
            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6">Scoring</Typography>

                <Stack spacing={2} sx={{ mt: 1 }}>
                    {/* Explanation */}
                    <Typography variant="body2" color="text.secondary">
                        Each region has a <b>weight</b> (set in Region Properties). When the student
                        places a point inside a region, that region&apos;s weight is added to their
                        score. The total is compared against the <b>target weight</b> below.
                    </Typography>

                    {isSimpleCounting && (
                        <Alert severity="info">
                            All regions have the same weight &mdash; this is a simple counting
                            question. The target weight equals the number of correct selections
                            needed.
                        </Alert>
                    )}

                    {/* Target weight */}
                    <ControlledTextField
                        name="targetWeight"
                        control={control}
                        label="Target weight"
                        type="number"
                        error={errors.targetWeight}
                        inputProps={{ min: 0 }}
                    />

                    {/* Match mode */}
                    <ControlledSingleSelect
                        name="matchMode"
                        control={control}
                        options={[
                            { id: 'exact', name: 'Exactly equals target' },
                            { id: 'atLeast', name: 'At least target' },
                            { id: 'atMost', name: 'At most target' },
                        ]}
                        label="Match mode"
                        error={errors.matchMode}
                    />

                    {/* Summary */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Scoring Summary
                        </Typography>

                        <Typography variant="body2">
                            Student gets <b>{maxSelections}</b> click
                            {maxSelections !== 1 ? 's' : ''}. The sum of selected region weights
                            must be{' '}
                            <b>
                                {matchMode === 'exact' && `exactly ${targetWeight}`}
                                {matchMode === 'atLeast' && `at least ${targetWeight}`}
                                {matchMode === 'atMost' && `at most ${targetWeight}`}
                            </b>
                            .
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                        >
                            Max achievable weight (positive regions): {maxPossibleWeight}
                        </Typography>
                    </Paper>

                    {/* Warnings */}
                    {warnings.map((w, i) => (
                        <Alert key={i} severity="warning">
                            {w}
                        </Alert>
                    ))}

                    {hasNegativeWeights && (
                        <Alert severity="info">
                            Some regions have negative weights. Selecting those regions will reduce
                            the student&apos;s score.
                        </Alert>
                    )}

                    {/* Region weight overview */}
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Region Weights
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Edit weights in the Region Properties panel. This is a read-only summary.
                    </Typography>

                    <Stack spacing={0.5} sx={{ overflowY: 'auto', maxHeight: '40vh' }}>
                        {(regions ?? []).map((region) => (
                            <Paper
                                key={region.id}
                                variant="outlined"
                                sx={{
                                    p: 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderColor:
                                        Number(region.weight) > 0
                                            ? '#4caf50'
                                            : Number(region.weight) < 0
                                              ? '#f44336'
                                              : '#ccc',
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2">{region.label}</Typography>
                                    {!region.visible && (
                                        <Typography variant="caption" color="warning.main">
                                            (hidden)
                                        </Typography>
                                    )}
                                </Stack>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color:
                                            Number(region.weight) > 0
                                                ? 'success.main'
                                                : Number(region.weight) < 0
                                                  ? 'error.main'
                                                  : 'text.secondary',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {Number(region.weight) > 0 ? '+' : ''}
                                    {region.weight}
                                </Typography>
                            </Paper>
                        ))}
                        {(!regions || regions.length === 0) && (
                            <Typography variant="body2" color="text.secondary">
                                Add regions first.
                            </Typography>
                        )}
                    </Stack>

                    <ErrorMessage error={errors.regions?.root} />
                </Stack>
            </Box>
        </Drawer>
    );
}
